<template>
  <component
    :is="layoutComponent"
    page-title="Field Tickets"
    page-subtitle="Paper-style wellsite tickets — labor, equipment, materials"
  >
    <div class="ft-page pt-2">
      <!-- LIST -->
      <template v-if="view === 'list'">
        <div class="list-toolbar no-print">
          <div>
            <h1 class="text-2xl font-bold text-white">My Field Tickets</h1>
            <p class="text-slate-400 text-sm mt-1">Create a paper ticket on location, then submit for company-man approval.</p>
          </div>
          <button class="btn primary" type="button" @click="startNew">+ New Ticket</button>
        </div>

        <div v-if="errorMessage" class="notice error no-print">{{ errorMessage }}</div>
        <div v-if="saved" class="notice success no-print">{{ saved }}</div>
        <div v-if="loading" class="notice no-print">Loading…</div>

        <div v-else class="list-panel">
          <div v-if="!tickets.length" class="empty">No tickets yet. Create one from the pad.</div>
          <table v-else class="list-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Well / Site</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in tickets" :key="t.id">
                <td>
                  <b>{{ t.number }}</b>
                  <div class="muted">{{ t.serviceType || t.title }}</div>
                </td>
                <td>{{ t.wellName || t.siteName || t.customerName || '—' }}</td>
                <td>{{ t.workDate || t.createdAt }}</td>
                <td class="money">${{ Number(t.totalAmount || 0).toLocaleString() }}</td>
                <td class="capitalize">{{ String(t.status || '').replace('_', ' ') }}</td>
                <td class="actions-cell">
                  <button type="button" class="link" @click="openTicket(t.id)">
                    {{ ['open', 'in_progress'].includes(t.status) ? 'Edit' : 'View' }}
                  </button>
                  <button
                    v-if="['open', 'in_progress'].includes(t.status)"
                    type="button"
                    class="link"
                    @click="submitTicket(t.id)"
                  >Submit</button>
                  <button
                    v-if="isContractorTickets && !t.invoiceId && !['cancelled'].includes(t.status)"
                    type="button"
                    class="link"
                    :disabled="invoicingId === t.id"
                    @click="invoiceTicket(t)"
                  >{{ invoicingId === t.id ? 'Invoicing…' : 'Invoice' }}</button>
                  <button
                    v-if="isContractorTickets && t.invoiceId"
                    type="button"
                    class="link"
                    @click="router.push(`/invoices?id=${t.invoiceId}`)"
                  >View Invoice</button>
                  <button
                    v-if="['open', 'in_progress'].includes(t.status)"
                    type="button"
                    class="link danger"
                    :disabled="deletingId === t.id"
                    @click="deleteTicket(t)"
                  >{{ deletingId === t.id ? 'Deleting…' : 'Delete' }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- PAPER FORM -->
      <template v-else>
        <div class="toolbar no-print">
          <button type="button" class="back" @click="backToList">← Back to Field Tickets</button>
          <div class="actions">
            <select v-if="sites.length" v-model="siteId" class="site-select" @change="applySite">
              <option :value="0">Link site…</option>
              <option v-for="s in sites" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <button type="button" class="btn secondary" @click="printTicket">Print / PDF</button>
            <button
              v-if="isContractorTickets && ticketId && !ticketInvoiceId"
              type="button"
              class="btn secondary"
              :disabled="invoicingId === ticketId"
              @click="invoiceTicket({ id: ticketId, number: form.ticket_number })"
            >{{ invoicingId === ticketId ? 'Invoicing…' : 'Create Invoice' }}</button>
            <button
              v-if="canEdit"
              type="button"
              class="btn secondary"
              :disabled="saving"
              @click="saveTicket(false)"
            >{{ saving ? 'Saving…' : 'Save Draft' }}</button>
            <button
              v-if="canEdit"
              type="button"
              class="btn primary"
              :disabled="saving"
              @click="saveTicket(true)"
            >{{ saving ? 'Submitting…' : 'Submit Ticket' }}</button>
          </div>
        </div>

        <div v-if="saved" class="notice success no-print">{{ saved }}</div>
        <div v-if="errorMessage" class="notice error no-print">{{ errorMessage }}</div>
        <div v-if="ticketStatus" class="notice no-print">Status: <b class="capitalize">{{ ticketStatus }}</b></div>

        <section class="ticket-paper" :class="{ readonly: !canEdit }">
          <header class="ticket-header">
            <div class="brand-box">
              <input ref="logoInput" id="ticket-logo" class="sr-only" type="file" accept="image/*" :disabled="!canEdit" @change="onLogoChange" />
              <label for="ticket-logo" class="logo-upload no-print">
                <img v-if="logoPreview" :src="logoPreview" alt="Company logo" />
                <div v-else>
                  <strong>Company Logo</strong>
                  <span>Upload</span>
                </div>
              </label>
              <div v-if="logoPreview" class="print-logo print-only">
                <img :src="logoPreview" alt="Company logo" />
              </div>
              <input v-model="form.company_name" class="company-name" placeholder="SERVICE COMPANY NAME" :readonly="!canEdit" />
              <button v-if="logoPreview && canEdit" type="button" class="remove-logo no-print" @click="clearLogo">Remove Logo</button>
            </div>
            <div class="ticket-title">
              <h1>FIELD TICKET</h1>
              <div class="ticket-no">
                <span>Ticket No.</span>
                <input v-model="form.ticket_number" placeholder="FT-000001" :readonly="!canEdit" />
              </div>
            </div>
          </header>

          <div class="top-grid">
            <label><span>Customer</span><input v-model="form.customer" :readonly="!canEdit" /></label>
            <label><span>Operator</span><input v-model="form.operator" :readonly="!canEdit" /></label>
            <label><span>Well Name / No.</span><input v-model="form.well_name" :readonly="!canEdit" /></label>
            <label><span>Lease / Location</span><input v-model="form.lease_location" :readonly="!canEdit" /></label>
            <label><span>County / State</span><input v-model="form.county_state" :readonly="!canEdit" /></label>
            <label><span>Date</span><input v-model="form.work_date" type="date" :readonly="!canEdit" /></label>
          </div>

          <div class="mid-grid">
            <label><span>PO #</span><input v-model="form.po_number" :readonly="!canEdit" /></label>
            <label><span>AFE #</span><input v-model="form.afe_number" :readonly="!canEdit" /></label>
            <label><span>Job #</span><input v-model="form.job_number" :readonly="!canEdit" /></label>
            <label><span>Rig / Unit</span><input v-model="form.rig_name" :readonly="!canEdit" /></label>
          </div>

          <div class="service-grid">
            <label class="wide">
              <span>Service Performed</span>
              <input v-model="form.service_type" :readonly="!canEdit" />
            </label>
            <label><span>Start Time</span><input v-model="form.start_time" type="time" :readonly="!canEdit" /></label>
            <label><span>End Time</span><input v-model="form.end_time" type="time" :readonly="!canEdit" /></label>
            <label><span>Supervisor</span><input v-model="form.supervisor" :readonly="!canEdit" /></label>
            <label><span>Company Man</span><input v-model="form.company_man" :readonly="!canEdit" /></label>
          </div>

          <div class="email-row no-print" v-if="canEdit">
            <label>
              <span>Company Man Email (for approval)</span>
              <input v-model="form.company_man_email" type="email" placeholder="companyman@operator.com" />
            </label>
          </div>

          <section class="line-items">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee / Equipment</th>
                  <th>Description of Work / Service</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th class="no-print"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in form.line_items" :key="index">
                  <td><input v-model="item.date" type="date" :readonly="!canEdit" /></td>
                  <td><input v-model="item.employee" :readonly="!canEdit" /></td>
                  <td><input v-model="item.description" :readonly="!canEdit" /></td>
                  <td><input v-model.number="item.quantity" type="number" step="0.01" :readonly="!canEdit" @input="recalc(item)" /></td>
                  <td><input v-model="item.unit" placeholder="HR / DAY / EA" :readonly="!canEdit" /></td>
                  <td><input v-model.number="item.rate" type="number" step="0.01" :readonly="!canEdit" @input="recalc(item)" /></td>
                  <td><input v-model="item.amount" type="number" step="0.01" :readonly="!canEdit" /></td>
                  <td class="no-print">
                    <button v-if="canEdit" type="button" class="remove-line" @click="removeLine(index)">×</button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="6" class="total-label">TOTAL</td>
                  <td class="total-value">${{ subtotal.toFixed(2) }}</td>
                  <td class="no-print"></td>
                </tr>
              </tfoot>
            </table>
            <button v-if="canEdit" type="button" class="add-line no-print" @click="addLine">+ Add Line</button>
          </section>

          <section class="notes-box">
            <span>Job Notes / Work Performed / Equipment / Materials</span>
            <textarea v-model="form.notes" rows="5" :readonly="!canEdit"></textarea>
          </section>

          <section class="approval">
            <div class="approval-copy">
              I certify that the above labor, equipment, materials and services were furnished as stated and were received in satisfactory condition unless noted above.
            </div>
            <div class="signature-grid">
              <label>
                <span>Customer / Company Man Name</span>
                <input v-model="form.customer_signature_name" :readonly="!canEdit" />
              </label>
              <label>
                <span>Customer / Company Man Signature</span>
                <input v-model="form.customer_signature" placeholder="Type name or connect signature pad" :readonly="!canEdit" />
              </label>
              <label>
                <span>Service Company Supervisor Signature</span>
                <input v-model="form.supervisor_signature" placeholder="Type name or connect signature pad" :readonly="!canEdit" />
              </label>
            </div>
          </section>

          <footer class="ticket-footer">
            <strong>FIELD COPY / OFFICE COPY</strong>
            <span>Generated with OilBuddyAI</span>
          </footer>
        </section>
      </template>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import DriverAppLayout from '@/components/layout/DriverAppLayout.vue'
import { buildApiUrl } from '@/config/api'

const route = useRoute()
const router = useRouter()
const isContractorTickets = computed(() => route.path.startsWith('/field-tickets'))
const ticketsApi = computed(() =>
  isContractorTickets.value ? 'contractor/field-tickets' : 'employee/field-tickets',
)
const layoutComponent = computed(() => route.path.startsWith('/driver') ? DriverAppLayout : AppLayout)

const view = ref<'list' | 'form'>('list')
const loading = ref(true)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const invoicingId = ref<number | null>(null)
const ticketInvoiceId = ref<number | null>(null)
const saved = ref('')
const errorMessage = ref('')
const tickets = ref<any[]>([])
const sites = ref<any[]>([])
const siteId = ref(0)
const ticketId = ref<number | null>(null)
const ticketStatus = ref('open')
const logoInput = ref<HTMLInputElement | null>(null)
const logoPreview = ref<string | null>(null)
const logoFile = ref<File | null>(null)
const defaults = ref({ companyName: '', supervisor: '', workDate: '', serviceType: 'Flowback / Well Testing' })

const lineItem = () => ({
  date: '',
  employee: '',
  description: '',
  quantity: '' as any,
  unit: '',
  rate: '' as any,
  amount: '' as any,
})

const form = reactive({
  ticket_number: '',
  company_name: '',
  customer: '',
  operator: '',
  well_name: '',
  lease_location: '',
  county_state: '',
  po_number: '',
  afe_number: '',
  job_number: '',
  work_date: new Date().toISOString().slice(0, 10),
  service_type: 'Flowback / Well Testing',
  start_time: '',
  end_time: '',
  supervisor: '',
  company_man: '',
  company_man_email: '',
  rig_name: '',
  notes: '',
  line_items: [lineItem(), lineItem(), lineItem(), lineItem(), lineItem()] as any[],
  customer_signature_name: '',
  customer_signature: '',
  supervisor_signature: '',
})

const canEdit = computed(() => !ticketId.value || ['open', 'in_progress'].includes(ticketStatus.value))
const subtotal = computed(() =>
  form.line_items.reduce((sum, item) => sum + Number(item.amount || 0), 0),
)

function authHeaders(json = true): Record<string, string> {
  const h: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
  if (json) h['Content-Type'] = 'application/json'
  return h
}

function resetForm() {
  Object.assign(form, {
    ticket_number: '',
    company_name: defaults.value.companyName || '',
    customer: '',
    operator: '',
    well_name: '',
    lease_location: '',
    county_state: '',
    po_number: '',
    afe_number: '',
    job_number: '',
    work_date: defaults.value.workDate || new Date().toISOString().slice(0, 10),
    service_type: defaults.value.serviceType || 'Flowback / Well Testing',
    start_time: '',
    end_time: '',
    supervisor: defaults.value.supervisor || '',
    company_man: '',
    company_man_email: '',
    rig_name: '',
    notes: '',
    line_items: [lineItem(), lineItem(), lineItem(), lineItem(), lineItem()],
    customer_signature_name: '',
    customer_signature: '',
    supervisor_signature: '',
  })
  if (form.line_items[0]) {
    form.line_items[0].date = form.work_date
    form.line_items[0].employee = defaults.value.supervisor || ''
    form.line_items[0].unit = 'HR'
  }
  ticketId.value = null
  ticketInvoiceId.value = null
  ticketStatus.value = 'open'
  siteId.value = 0
  clearLogo()
}

function hydrateFromTicket(t: any) {
  ticketId.value = t.id
  ticketInvoiceId.value = t.invoiceId || null
  ticketStatus.value = t.status || 'open'
  siteId.value = t.siteId || 0
  form.ticket_number = t.number || ''
  form.company_name = t.companyDisplayName || defaults.value.companyName || ''
  form.customer = t.customerName || ''
  form.operator = t.operatorName || ''
  form.well_name = t.wellName || ''
  form.lease_location = t.leaseLocation || t.siteName || ''
  form.county_state = t.countyState || ''
  form.po_number = t.poNumber || ''
  form.afe_number = t.afeNumber || ''
  form.job_number = t.jobNumber || ''
  form.work_date = t.workDate || new Date().toISOString().slice(0, 10)
  form.service_type = t.serviceType || t.title || 'Flowback / Well Testing'
  form.start_time = t.startTime || ''
  form.end_time = t.endTime || ''
  form.supervisor = t.supervisorName || defaults.value.supervisor || ''
  form.company_man = t.companyManName || ''
  form.company_man_email = t.companyManEmail || ''
  form.rig_name = t.rigName || ''
  form.notes = t.fieldNotes || t.description || ''
  form.customer_signature_name = t.customerSignatureName || ''
  form.customer_signature = t.customerSignature || ''
  form.supervisor_signature = t.supervisorSignature || ''
  const items = (t.line_items || t.lines || []).map((l: any) => ({
    date: l.date || '',
    employee: l.employee || '',
    description: l.description || '',
    quantity: l.quantity ?? '',
    unit: l.unit || '',
    rate: l.rate ?? '',
    amount: l.amount ?? '',
  }))
  form.line_items = items.length ? items : [lineItem(), lineItem(), lineItem()]
  logoPreview.value = t.companyLogoUrl || t.companyLogoPath || null
  logoFile.value = null
}

async function loadList() {
  loading.value = true
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(ticketsApi.value), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    tickets.value = data.fieldTickets || []
    sites.value = data.sites || []
    if (data.defaults) defaults.value = { ...defaults.value, ...data.defaults }
  } catch (e: any) {
    errorMessage.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

function startNew() {
  resetForm()
  view.value = 'form'
}

function backToList() {
  view.value = 'list'
  loadList()
}

async function openTicket(id: number) {
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`${ticketsApi.value}/${id}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to open ticket')
    hydrateFromTicket(data.ticket)
    view.value = 'form'
  } catch (e: any) {
    errorMessage.value = e.message || 'Failed to open ticket'
  }
}

function applySite() {
  const site = sites.value.find((s) => s.id === Number(siteId.value))
  if (site) {
    form.lease_location = site.name || form.lease_location
    if (site.location && !form.county_state) form.county_state = site.location
  }
}

function onLogoChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  logoFile.value = file
  if (logoPreview.value?.startsWith?.('blob:')) URL.revokeObjectURL(logoPreview.value)
  logoPreview.value = URL.createObjectURL(file)
}

function clearLogo() {
  logoFile.value = null
  if (logoPreview.value?.startsWith?.('blob:')) URL.revokeObjectURL(logoPreview.value)
  logoPreview.value = null
  if (logoInput.value) logoInput.value.value = ''
}

function addLine() {
  form.line_items.push(lineItem())
}

function removeLine(index: number) {
  if (form.line_items.length > 1) form.line_items.splice(index, 1)
}

function recalc(item: any) {
  const qty = Number(item.quantity || 0)
  const rate = Number(item.rate || 0)
  item.amount = (qty * rate).toFixed(2)
}

function buildPayload() {
  return {
    ticket_number: form.ticket_number,
    company_name: form.company_name,
    customer: form.customer,
    operator: form.operator,
    well_name: form.well_name,
    lease_location: form.lease_location,
    county_state: form.county_state,
    po_number: form.po_number,
    afe_number: form.afe_number,
    job_number: form.job_number,
    work_date: form.work_date,
    service_type: form.service_type,
    title: form.service_type || 'Field Ticket',
    start_time: form.start_time,
    end_time: form.end_time,
    supervisor: form.supervisor,
    company_man: form.company_man,
    company_man_email: form.company_man_email,
    rig_name: form.rig_name,
    notes: form.notes,
    siteId: siteId.value || null,
    siteName: form.lease_location || null,
    customer_signature_name: form.customer_signature_name,
    customer_signature: form.customer_signature,
    supervisor_signature: form.supervisor_signature,
    line_items: form.line_items,
  }
}

async function saveTicket(submitAfter: boolean) {
  if (!canEdit.value) return
  saving.value = true
  saved.value = ''
  errorMessage.value = ''
  try {
    const fd = new FormData()
    const payload = buildPayload()
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'line_items') fd.append('line_items', JSON.stringify(value))
      else if (value !== null && value !== undefined) fd.append(key, String(value))
    })
    if (logoFile.value) fd.append('company_logo', logoFile.value)

    const url = ticketId.value
      ? buildApiUrl(`${ticketsApi.value}/${ticketId.value}`)
      : buildApiUrl(ticketsApi.value)
    const res = await fetch(url, { method: 'POST', headers: authHeaders(false), body: fd })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || data.message || 'Could not save field ticket.')

    const t = data.ticket
    if (t) hydrateFromTicket(t)
    logoFile.value = null
    saved.value = data.message || 'Field ticket saved.'

    if (submitAfter && ticketId.value) {
      const sub = await fetch(buildApiUrl(`${ticketsApi.value}/${ticketId.value}/submit`), {
        method: 'POST',
        headers: authHeaders(),
        body: '{}',
      })
      const subData = await sub.json()
      if (!sub.ok) throw new Error(subData.error || 'Submit failed')
      if (subData.ticket) hydrateFromTicket(subData.ticket)
      saved.value = 'Field ticket submitted for approval.'
    }

    setTimeout(() => (saved.value = ''), 2500)
  } catch (e: any) {
    errorMessage.value = e.message || 'Field ticket could not be saved.'
  } finally {
    saving.value = false
  }
}

async function submitTicket(id: number) {
  errorMessage.value = ''
  saved.value = ''
  try {
    const res = await fetch(buildApiUrl(`${ticketsApi.value}/${id}/submit`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Submit failed')
    saved.value = 'Ticket submitted for approval'
    await loadList()
    setTimeout(() => (saved.value = ''), 2500)
  } catch (e: any) {
    errorMessage.value = e.message || 'Submit failed'
  }
}

async function deleteTicket(ticket: any) {
  if (!window.confirm(`Delete field ticket ${ticket.number || ''}? This cannot be undone.`)) return
  deletingId.value = Number(ticket.id)
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`${ticketsApi.value}/${ticket.id}`), {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    tickets.value = tickets.value.filter((item) => item.id !== ticket.id)
    if (ticketId.value === ticket.id) {
      ticketId.value = null
      view.value = 'list'
    }
  } catch (e: any) {
    errorMessage.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

async function invoiceTicket(ticket: any) {
  if (!ticket?.id) return
  invoicingId.value = Number(ticket.id)
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`${ticketsApi.value}/${ticket.id}/invoice`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Could not create invoice')
    ticketInvoiceId.value = data.id || ticketInvoiceId.value
    saved.value = `Invoice ${data.invoiceNumber || ''} created`
    await loadList()
    if (data.id) router.push(`/invoices?id=${data.id}`)
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not create invoice'
  } finally {
    invoicingId.value = null
  }
}

function printTicket() {
  window.print()
}

onMounted(loadList)
</script>

<style scoped>
.ft-page { color: #dce8f4; }
.list-toolbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.list-panel { background: #162438; border: 1px solid #2d425a; border-radius: 12px; overflow: hidden; }
.list-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.list-table th, .list-table td { padding: 12px 14px; border-bottom: 1px solid #2a3d54; text-align: left; }
.list-table th { color: #8ea5bb; font-size: 11px; text-transform: uppercase; }
.list-table td { color: #e4eef7; }
.list-table .muted { color: #7892aa; font-size: 12px; margin-top: 2px; }
.list-table .money { color: #58dfc3; font-weight: 700; }
.actions-cell { display: flex; gap: 12px; }
.link { background: none; border: 0; color: #58dfc3; font-weight: 700; cursor: pointer; padding: 0; }
.link.danger { color: #fb7185; }
.empty { padding: 40px; text-align: center; color: #7892aa; }
.toolbar { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 14px; flex-wrap: wrap; }
.back { background: none; border: 0; color: #88afd1; cursor: pointer; font-size: 13px; padding: 0; }
.actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.site-select { background: #23344a; border: 1px solid #344a62; color: #eaf3fa; border-radius: 8px; padding: 8px 10px; }
.btn { border: 1px solid #344a62; border-radius: 8px; padding: 9px 14px; font-weight: 800; cursor: pointer; }
.secondary { background: #23344a; color: #e9f3fb; }
.primary { background: #087a66; color: #fff; border-color: #0d947a; }
.btn:disabled { opacity: .6; }
.notice { margin: 0 0 10px; padding: 9px 12px; border-radius: 8px; font-size: 13px; }
.success { background: #103c35; border: 1px solid #1d7768; color: #83e4d2; }
.error { background: #411b25; border: 1px solid #8c3d4a; color: #ffc8d0; }

.ticket-paper { background: #f3efe2; color: #1d1d1a; border: 1px solid #b7b09e; box-shadow: 0 24px 70px rgba(0,0,0,.28); border-radius: 4px; overflow: hidden; }
.ticket-header { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 3px double #444; }
.brand-box { padding: 12px 14px; display: grid; grid-template-columns: 180px 1fr; grid-template-rows: 70px auto; gap: 8px 12px; align-items: center; border-right: 2px solid #555; }
.logo-upload { height: 68px; border: 2px dashed #777; background: #faf7ec; display: flex; align-items: center; justify-content: center; cursor: pointer; text-align: center; }
.logo-upload img, .print-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.logo-upload span { display: block; font-size: 11px; color: #666; }
.company-name { font-size: 20px; font-weight: 900; text-transform: uppercase; border: 0; border-bottom: 2px solid #333; background: transparent; padding: 8px; color: #1d1d1a; }
.remove-logo { grid-column: 1 / 2; border: 0; background: none; color: #8a3b3b; font-size: 11px; cursor: pointer; }
.ticket-title { padding: 15px 18px; display: flex; align-items: center; justify-content: space-between; }
.ticket-title h1 { font-size: 32px; letter-spacing: .08em; margin: 0; color: #1d1d1a; }
.ticket-no { display: flex; align-items: center; gap: 8px; font-weight: 900; }
.ticket-no input { width: 150px; }

.top-grid, .mid-grid, .service-grid { display: grid; border-bottom: 2px solid #555; }
.top-grid { grid-template-columns: repeat(3, 1fr); }
.mid-grid { grid-template-columns: repeat(4, 1fr); }
.service-grid { grid-template-columns: 2fr repeat(4, 1fr); }
.top-grid label, .mid-grid label, .service-grid label, .email-row label { padding: 7px 9px; border-right: 1px solid #777; }
.top-grid label:nth-child(3n), .mid-grid label:nth-child(4n), .service-grid label:last-child { border-right: 0; }
.email-row { border-bottom: 2px solid #555; background: #ebe6d8; }
.email-row label { display: block; border-right: 0; }
label span, .notes-box > span { display: block; font-size: 10px; font-weight: 900; text-transform: uppercase; color: #48443b; margin-bottom: 3px; }
.ticket-paper input, .ticket-paper textarea { width: 100%; box-sizing: border-box; border: 0; border-bottom: 1px solid #777; background: transparent; color: #171714; padding: 5px 3px; outline: none; font-size: 13px; }

.line-items { padding: 0; }
.line-items table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.line-items th, .line-items td { border-right: 1px solid #666; border-bottom: 1px solid #666; }
.line-items th { background: #ded8c7; padding: 7px 5px; font-size: 10px; text-transform: uppercase; color: #333; }
.line-items td { height: 38px; }
.line-items input { height: 37px; border: 0; padding: 5px; text-align: center; }
.line-items th:nth-child(1) { width: 9%; }
.line-items th:nth-child(2) { width: 17%; }
.line-items th:nth-child(3) { width: 31%; }
.line-items th:nth-child(4) { width: 8%; }
.line-items th:nth-child(5) { width: 8%; }
.line-items th:nth-child(6) { width: 9%; }
.line-items th:nth-child(7) { width: 10%; }
.line-items th:last-child { width: 4%; }
.remove-line { border: 0; background: none; color: #a53b3b; font-size: 18px; cursor: pointer; }
.add-line { margin: 8px; border: 1px dashed #666; background: #ebe6d8; padding: 6px 10px; cursor: pointer; color: #333; }
.total-label { text-align: right !important; font-weight: 900; padding-right: 12px !important; color: #1d1d1a; }
.total-value { font-weight: 900; text-align: center; background: #e8e1cf; color: #1d1d1a; }

.notes-box { padding: 9px; border-bottom: 2px solid #555; }
.notes-box textarea { border: 1px solid #777; background: rgba(255,255,255,.2); resize: vertical; padding: 8px; }
.approval-copy { padding: 9px 12px; font-size: 10px; border-bottom: 1px solid #666; background: #e6e0cf; color: #333; }
.signature-grid { display: grid; grid-template-columns: repeat(3, 1fr); }
.signature-grid label { padding: 10px; border-right: 1px solid #777; }
.signature-grid label:last-child { border-right: 0; }
.signature-grid input { font-family: "Segoe Script", cursive; font-size: 15px; }
.ticket-footer { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-top: 2px solid #555; font-size: 10px; background: #ddd6c4; color: #333; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
.print-only { display: none; }
.readonly input, .readonly textarea { opacity: .9; }

@media (max-width: 1000px) {
  .top-grid { grid-template-columns: 1fr 1fr; }
  .mid-grid { grid-template-columns: 1fr 1fr; }
  .service-grid { grid-template-columns: 1fr 1fr; }
  .ticket-header { grid-template-columns: 1fr; }
  .brand-box { border-right: 0; border-bottom: 2px solid #555; }
  .signature-grid { grid-template-columns: 1fr; }
  .ticket-paper { overflow-x: auto; }
  .line-items table { min-width: 1100px; }
}
@media print {
  :global(body) { background: #fff !important; }
  .ft-page { padding: 0; color: #000; }
  .no-print { display: none !important; }
  .print-only { display: block; }
  .ticket-paper { max-width: none; box-shadow: none; border: 0; }
  .brand-box { position: relative; }
  .print-logo { width: 180px; height: 68px; }
  .ticket-paper, .ticket-paper input, .ticket-paper textarea { background: #fff !important; color: #000 !important; }
  @page { size: landscape; margin: 7mm; }
}
</style>
